import { HireFlowMenuItems } from "@/components/employee/HireFlowButton";
import { EmployeeAvatar } from "@/components/employee/EmployeeAvatar.tsx";
import { UserCard } from "@/components/user-card/UserCard.tsx";
import useFormatter from "@/hooks/use-formatter.hook.ts";
import usePermissions from "@/hooks/use-permissions.hook.ts";
import { EmployeePageComponentProps } from "@/pages/employees/employee/components/employee-page-components.types.ts";
import { EmployeeViewModal } from "@/pages/employees/widgets/EmployeeViewModal.tsx";
import { useEmployees } from "@/queries/employees/use-employees.query.ts";
import { getEmployeePositionTitle } from "@/types/employees/employee-position.types.ts";
import {
  ApiEmployeeFilter,
  getActiveEmployeeLifecycleStatuses,
} from "@/types/employees/employee.types.ts";
import { ApiPermission, EmployeeFieldPermission } from "@/types/role.types.ts";
import {
  Box,
  Button,
  DropdownMenu,
  DropdownMenuItem,
  DropdownMenuSub,
  EmptyStateBase,
  FontAwesomeIcon,
  PaginationBlock,
  Skeleton,
  Stack,
  Title,
  useModals,
} from "@skailer-com/uikit";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

const defaultVisibleDirectReportsCount = 8;

export function EmployeePageOverviewDirectReports({ employee }: EmployeePageComponentProps) {
  const { t } = useTranslation();
  const { canCurrentEmployeeEditField, canIEdit } = usePermissions();
  const { renderEmployeeName } = useFormatter();
  const { openModal } = useModals();
  const navigate = useNavigate();
  const [isShowAllDirectReports, setIsShowAllDirectReports] = useState(false);

  const [filter, setFilter] = useState<ApiEmployeeFilter>({
    page_number: 1,
    manager_ids: [employee.id],
    employee_lifecycle_statuses: getActiveEmployeeLifecycleStatuses(),
  });

  const [syncedEmployeeId, setSyncedEmployeeId] = useState(employee.id);

  if (employee.id !== syncedEmployeeId) {
    setSyncedEmployeeId(employee.id);
    setFilter((prevFilter) => ({
      ...prevFilter,
      manager_ids: [employee.id],
      page_number: 1,
    }));
    setIsShowAllDirectReports(false);
  }

  const { data: myEmployees, isLoading: isMyEmployeesIsLoading } = useEmployees(filter);
  const directReportsCount = myEmployees?.metadata.total_items ?? 0;
  const visibleDirectReports = isShowAllDirectReports
    ? myEmployees?.items
    : myEmployees?.items?.slice(0, defaultVisibleDirectReportsCount);
  const remainingDirectReportsCount = Math.max(
    directReportsCount - defaultVisibleDirectReportsCount,
    0
  );
  const isEmpty = !isMyEmployeesIsLoading && directReportsCount === 0;
  const shouldShowMoreButton =
    !isMyEmployeesIsLoading &&
    !isEmpty &&
    !isShowAllDirectReports &&
    remainingDirectReportsCount > 0;
  const canViewOrgChart = canCurrentEmployeeEditField(
    EmployeeFieldPermission.see_organization_structure
  );
  const canAddEmployee = canIEdit(ApiPermission.add_employees);
  const canShowEmptyStateDetails = canAddEmployee;
  const hasHeaderActions = canViewOrgChart || canAddEmployee;
  const titleCaption = isMyEmployeesIsLoading
    ? "..."
    : isEmpty
      ? undefined
      : t("plurals:employees_count", { count: directReportsCount });

  const renderOverview = () => {
    return (
      <>
        <Stack direction={"vertical"} gap={"md"}>
          {hasHeaderActions ? (
            <Stack direction={"horizontal"} justify={"between"} items={"center"} gap={"md"}>
              <Title size={"lg"} header={t("core:direct_reports")} caption={titleCaption} />
              <DropdownMenu
                trigger={
                  <Button
                    variant={"secondary"}
                    size={"md"}
                    rightIcon={<FontAwesomeIcon icon="fa-light fa-chevron-down" />}
                  >
                    {t("common:actions")}
                  </Button>
                }
              >
                {canViewOrgChart && (
                  <DropdownMenuItem
                    to={`/employees/chart?employee_id=${employee.id}`}
                    target="_blank"
                    leftIcon={<FontAwesomeIcon icon="fa-light fa-sitemap" />}
                    title={t("core:org_chart_employees")}
                  />
                )}
                {canAddEmployee && (
                  <DropdownMenuSub
                    title={t("core:create_teammate")}
                    leftIcon={<FontAwesomeIcon icon="fa-light fa-plus" />}
                  >
                    <HireFlowMenuItems target="_blank" />
                  </DropdownMenuSub>
                )}
              </DropdownMenu>
            </Stack>
          ) : (
            <Title size={"lg"} header={t("core:direct_reports")} caption={titleCaption} />
          )}

          {isMyEmployeesIsLoading && (
            <Stack direction={"horizontal"} gap={"md"}>
              <Stack className={"w-41"} gap={"sm"}>
                <Skeleton className={"w-41 h-41 rounded-lg"} />
                <Skeleton className={"w-40 h-4"} />
                <Skeleton className={"w-40 h-4"} />
              </Stack>
              <Stack className={"w-41"} gap={"sm"}>
                <Skeleton className={"w-41 h-41 rounded-lg"} />
                <Skeleton className={"w-40 h-4"} />
                <Skeleton className={"w-40 h-4"} />
              </Stack>
              <Stack className={"w-41"} gap={"sm"}>
                <Skeleton className={"w-41 h-41 rounded-lg"} />
                <Skeleton className={"w-40 h-4"} />
                <Skeleton className={"w-40 h-4"} />
              </Stack>
              <Stack className={"w-41"} gap={"sm"}>
                <Skeleton className={"w-41 h-41 rounded-lg"} />
                <Skeleton className={"w-40 h-4"} />
                <Skeleton className={"w-40 h-4"} />
              </Stack>
            </Stack>
          )}

          {isEmpty ? (
            <EmptyStateBase
              variant="primary"
              title={t("core:employee_overview_no_direct_reports")}
              description={
                canShowEmptyStateDetails
                  ? t("core:employee_overview_no_direct_reports_description")
                  : undefined
              }
              emoji={":busts_in_silhouette:"}
              primaryButton={
                canShowEmptyStateDetails ? (
                  <Button
                    onClick={() => {
                      navigate("/employees");
                    }}
                  >
                    {t("core:employee_overview_no_direct_reports_button")}
                  </Button>
                ) : undefined
              }
            />
          ) : (
            <>
              <Box
                className={"grid gap-(--gap-md)"}
                style={{ gridTemplateColumns: "repeat(auto-fill, minmax(10rem, 1fr))" }}
              >
                {visibleDirectReports?.map((employeeItem) => (
                  <UserCard
                    onClick={() => {
                      openModal(EmployeeViewModal, {
                        employeeId: employeeItem.id,
                      });
                    }}
                    to={`/employees/${employeeItem.id}`}
                    key={`team-card-${employeeItem.id}`}
                    size={"md"}
                    className={"w-full"}
                    avatarElement={<EmployeeAvatar employee={employeeItem} size={"2xl"} />}
                    title={renderEmployeeName(employeeItem)}
                    caption={getEmployeePositionTitle(employeeItem)}
                  />
                ))}
              </Box>

              {shouldShowMoreButton && (
                <Stack className="pt-(--gap-md)" direction="horizontal">
                  <Button variant="secondary" onClick={() => setIsShowAllDirectReports(true)}>
                    {t("common:show_more_template", { count: remainingDirectReportsCount })}
                  </Button>
                </Stack>
              )}
            </>
          )}

          {!isEmpty &&
            !isMyEmployeesIsLoading &&
            isShowAllDirectReports &&
            (myEmployees?.metadata.total_pages ?? 0) > 1 && (
              <PaginationBlock
                metadata={myEmployees?.metadata}
                onPageChange={(page) => {
                  setFilter({ ...filter, page_number: page });
                }}
              />
            )}
        </Stack>
      </>
    );
  };

  return (
    <>
      <div className={"flex flex-row gap-9 py-0"}>
        <div className={"w-full overflow-hidden"}>{renderOverview()}</div>
      </div>
    </>
  );
}
